/**
 * @module DialogContent
 *
 * @see https://0.vuetifyjs.com/components/disclosure/dialog
 *
 * @remarks
 * Content component for dialogs. Renders the dialog panel using the native
 * dialog element with `showModal()` for proper modal behavior including
 * focus trapping, backdrop, and escape key handling.
 *
 * Integrates with createStack for z-index coordination when multiple
 * dialogs are open simultaneously.
 */

<script lang="ts">
  // Types
  import type { AtomProps } from '#v0/components/Atom'

  export interface DialogContentProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
    /** Close dialog when clicking outside content @default true */
    closeOnClickOutside?: boolean
    /**
     * Whether this dialog blocks scrim dismissal
     *
     * @default false
     * @remarks When true, clicking the scrim will not dismiss this dialog. Use for critical dialogs requiring explicit user action.
     */
    blocking?: boolean
    /**
     * Whether the global Scrim should paint a backdrop for this dialog
     *
     * @default true
     * @remarks When false, this dialog still stacks for z-index but `Scrim` skips it.
     * Use when the consumer owns its own backdrop (e.g. a design-system modal background).
     */
    scrim?: boolean
  }

  export interface DialogContentEmits {
    cancel: [e: Event]
    close: [e: Event]
  }

  export interface DialogContentSlotProps {
    /** Whether the dialog is currently open */
    isOpen: boolean
    /** Whether this dialog is the topmost in the global stack */
    globalTop: boolean
    /** Calculated z-index for the dialog */
    zIndex: number
    /** Attributes to bind to the dialog element */
    attrs: {
      'id': string
      'role': 'dialog'
      'aria-modal': 'true'
      'aria-labelledby': string | undefined
      'aria-describedby': string | undefined
      'style': { zIndex: number }
      'onCancel': (e: Event) => void
      'onClose': (e: Event) => void
    }
  }
</script>

<script setup lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Context
  import { useDialogContext } from './DialogRoot.vue'

  // Composables
  import { useClickOutside } from '#v0/composables/useClickOutside'
  import { useStack } from '#v0/composables/useStack'
  import { useToggleScope } from '#v0/composables/useToggleScope'

  // Utilities
  import { onMounted, toRef, useTemplateRef, watch } from 'vue'

  defineOptions({ name: 'DialogContent' })

  defineSlots<{
    default: (props: DialogContentSlotProps) => any
  }>()

  const {
    as = 'dialog',
    namespace = 'v0:dialog',
    closeOnClickOutside = true,
    blocking = false,
    scrim = true,
    renderless,
  } = defineProps<DialogContentProps>()

  const emit = defineEmits<DialogContentEmits>()

  const context = useDialogContext(namespace)

  const contentRef = useTemplateRef('content')

  // Register with global stack for z-index coordination
  const stack = useStack()
  const ticket = stack.register({
    onDismiss: () => context.close(),
    blocking: () => blocking,
    scrim: () => scrim,
    el: () => contentRef.value?.element,
  })

  watch(context.isOpen, isOpen => {
    if (isOpen) {
      ticket.select()
    } else {
      ticket.unselect()
    }
  }, { immediate: true })

  watch(context.isOpen, isOpen => {
    const element = contentRef.value?.element as HTMLDialogElement | undefined
    /* v8 ignore next -- defensive guard, element is always present after mount */
    if (!element) return

    if (isOpen) {
      element.showModal?.()
    } else {
      element.close?.()
    }
  })

  onMounted(() => {
    if (context.isOpen.value) {
      (contentRef.value?.element as HTMLDialogElement | undefined)?.showModal()
    }
  })

  useToggleScope(
    () => closeOnClickOutside && context.isOpen.value,
    /* v8 ignore start -- callback tested via useClickOutside */
    () => {
      useClickOutside(
        () => contentRef.value?.element,
        () => context.close(),
        { bounds: true },
      )
    },
    /* v8 ignore stop */
  )

  function onCancel (e: Event) {
    context.isOpen.value = false
    emit('cancel', e)
  }

  function onClose (e: Event) {
    context.isOpen.value = false
    emit('close', e)
  }

  const slotProps = toRef((): DialogContentSlotProps => ({
    isOpen: context.isOpen.value,
    globalTop: ticket.globalTop.value,
    zIndex: ticket.zIndex.value,
    attrs: {
      'id': context.id,
      'role': 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': context.hasTitle.value ? context.titleId : undefined,
      'aria-describedby': context.hasDescription.value ? context.descriptionId : undefined,
      'style': { zIndex: ticket.zIndex.value },
      'onCancel': onCancel,
      'onClose': onClose,
    },
  }))
</script>

<template>
  <Atom
    ref="content"
    :as
    :renderless
    v-bind="slotProps.attrs"
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
